import axios from 'axios';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL } from '../../config';
import { FacilitiesApi } from '@/api/clients/facilities/facilities.api';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import Button from '@/components/CustomButtons/Button';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import loader from '../../assets/img/loader.svg';

import { REFUND_TYPES } from '@/utility';
import TablePaginationActions from '@/views/Managefile/TablePaginationActions/TablePaginationActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { toast } from 'react-toastify';

const styles = {
  input: {
    height: '20px',
    width: '20px',
  },

  inputCursor: {
    cursor: 'pointer',
    height: '20px',
    width: '20px',
  },
};

const AchPayments = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [payments, setPayments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [facilities, setFacilities] = useState([]);
  const [facility, setFacility] = useState();
  const [paymentType, setPaymentType] = useState('ACH');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handlePaymentChange = (e) => {
    setPaymentType(e.target.value);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const getPaymentFileRecords = async () => {
    setLoading(true);
    const response = await axios.get(
      `${API_BASE_URL}/getPaymentFileRecords?${`paymentType=${paymentType}`}${
        facility ? `&facility=${facility}` : ''
      }`,
    );
    setLoading(false);
    setPayments(response.data);
  };
  const isAllChecked = () => {
    if (!payments.length) return false;
    return payments.every((payment, index) => {
      if (rowsPerPage < 0) {
        return payment.isChecked || payment.refund_status === 'Completed';
      }

      return (page + 1) * rowsPerPage > index
        ? payment.isChecked || payment.refund_status === 'Completed'
        : true;
    });
  };
  const isAllCompleted = () => {
    if (!payments.length) return false;
    return payments.every((payment, index) => {
      if (rowsPerPage < 0) {
        return payment.refund_status === 'Completed';
      }

      return (page + 1) * rowsPerPage > index
        ? payment.refund_status === 'Completed'
        : true;
    });
  };

  const warnToast = () => {
    toast.warning(t('achPayments.warnings.selectFacility'), {
      position: 'bottom-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const onAllCheck = (e) => {
    if (!facility) return warnToast();
    const newPayments = payments.map((payment, index) => {
      if (
        payment.refund_status === 'Pending' ||
        payment.refund_status === 'Pending-sent' ||
        payment.refund_status === 'ACH-sent' ||
        payment.refund_status === 'Pending-pp-sent'
      ) {
        if (rowsPerPage < 0) {
          payment.isChecked = e.target.checked;
          return payment;
        }
        if ((page + 1) * rowsPerPage > index)
          payment.isChecked = e.target.checked;
      }

      return payment;
    });
    setPayments(newPayments);
  };

  const handleOnCheck = (position, isChecked) => {
    if (!facility) return warnToast();
    setPayments((payment) => {
      const paymentArr = [...payment];
      paymentArr[position].isChecked = !isChecked;
      return paymentArr;
    });
  };

  const calculateTotalChecked = () => {
    return payments.reduce((total, current) => {
      if (current.isChecked) {
        return total + +current.refund_amount;
      }
      return total;
    }, 0);
  };

  const onGenerateFile = async () => {
    const ids = payments
      .filter((payment) => payment.isChecked)
      .map((payment) => payment.id);
    if (!ids.length) {
      return;
    }
    await axios.post(`${API_BASE_URL}/generateAchFile`, {
      ids,
      facility_id: facility,
      paymentType,
    });
    getPaymentFileRecords();
  };
  const selectFacility = (event) => {
    setFacility(event.target.value);
  };

  useEffect(() => {
    const fetchFacilities = async () => {
      const facilities = await FacilitiesApi.getAllFacilitiesPro();
      setFacilities(facilities);
    };
    fetchFacilities();
  }, []);

  useEffect(() => {
    const fetchPaymentRecords = async () => {
      getPaymentFileRecords();
    };
    fetchPaymentRecords();
  }, [facility, paymentType]);
  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='primary'>
            <h4>{t('achPayments.title')}</h4>
          </CardHeader>

          <CardBody>
            <div>
              {loading && (
                <div className='loaders'>
                  <img src={loader} />
                </div>
              )}
              <div className='t-header'>
                <div
                  className='t-header-left'
                  style={{ fontWeight: 'bold', fontSize: '15px' }}>
                  {t('achPayments.total', {
                    amount: parseFloat(calculateTotalChecked()).toLocaleString(
                      undefined,
                      {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      },
                    ),
                  })}
                </div>
                <div className='t-header-right'>
                  <div className='custom-search-box'>
                    <div className='custom-select'>
                      <select
                        className='full-width-elem'
                        id='Facility'
                        onChange={selectFacility}>
                        <option value='' style={{ fontWeight: 'bold' }}>
                          {t('achPayments.facilitySelect')}
                        </option>
                        {facilities.map((item) => {
                          return (
                            <>
                              <option
                                value={item.id}
                                className='parent'
                                style={{ fontWeight: 'bold' }}
                                key={item.id}>
                                {item.name}
                              </option>
                            </>
                          );
                        })}
                      </select>
                    </div>

                    <span className='search-icon'></span>
                  </div>
                  <div
                    style={{ margin: '0 10px', width: '200px' }}
                    className='custom-search-box'>
                    <div className='custom-select'>
                      <select
                        className='full-width-elem'
                        id='Facility'
                        onChange={handlePaymentChange}>
                        <option value='ACH' style={{ fontWeight: 'bold' }}>
                          {t('achPayments.paymentTypes.ach')}
                        </option>
                        <option
                          value='POSITIVE_PAY'
                          style={{ fontWeight: 'bold' }}>
                          {t('achPayments.paymentTypes.positivePayCheck')}
                        </option>
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                  <Button
                    style={{
                      cursor: 'pointer',
                    }}
                    color='primary'
                    id='dt'
                    onClick={onGenerateFile}>
                    {paymentType === 'ACH'
                      ? t('achPayments.generateButton.ach')
                      : paymentType === 'POSITIVE_PAY'
                      ? t('achPayments.generateButton.positivePayCheck')
                      : ''}
                  </Button>
                </div>
              </div>
              <Table width='100%' id='template'>
                <TableHead>
                  <TableRow>
                    <TableCell width='5%' align='left'>
                      <input
                        type='checkbox'
                        id='allSelect'
                        name='allSelect'
                        checked={isAllChecked()}
                        disabled={!payments.length || isAllCompleted()}
                        onChange={onAllCheck}
                        style={styles.inputCursor}
                      />
                    </TableCell>
                    <TableCell width='10%' align='left'>
                      {t('achPayments.table.date')}
                    </TableCell>
                    <TableCell width='15%' align='left'>
                      {t('achPayments.table.paymentCategory')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('achPayments.table.email')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('achPayments.table.phone')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('achPayments.table.amount')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('achPayments.table.reference')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('achPayments.table.paymentStatus')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                {payments && payments.length > 0 ? (
                  <TableBody>
                    {(rowsPerPage > 0
                      ? payments.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                      : payments
                    ).map((refund, index) => (
                      <TableRow className='txtalign' key={index}>
                        <TableCell>
                          <input
                            type='checkbox'
                            id={refund.id}
                            name={refund.first_name}
                            style={
                              refund.refund_status == 'Completed'
                                ? styles.input
                                : styles.inputCursor
                            }
                            checked={
                              refund.refund_status == 'Completed'
                                ? true
                                : refund.isChecked ?? false
                            }
                            disabled={refund.refund_status == 'Completed'}
                            onChange={() =>
                              handleOnCheck(index, refund.isChecked)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {refund &&
                            refund.status_updatedat &&
                            moment(refund.status_updatedat).format(
                              'MM-DD-YYYY',
                            )}
                        </TableCell>
                        <TableCell>
                          {refund.refund_type === REFUND_TYPES.BUSINESS
                            ? t('achPayments.refundTypes.business')
                            : refund.refund_type === REFUND_TYPES.INSURANCE
                            ? t('achPayments.refundTypes.insurance')
                            : refund.refund_type === REFUND_TYPES.REGULAR
                            ? t('achPayments.refundTypes.regular')
                            : refund.refund_type}
                        </TableCell>
                        <TableCell>
                          {refund.first_name} {refund.last_name}{' '}
                          {!!refund.email ||
                          ((refund.refund_type === REFUND_TYPES.INSURANCE ||
                            refund.refund_type === REFUND_TYPES.BUSINESS) &&
                            refund.business_payee_name) ? (
                            <span style={{ display: 'flex' }}>
                              <p style={{ fontSize: '12px' }}>
                                (
                                {refund.refund_type ===
                                  REFUND_TYPES.INSURANCE ||
                                refund.refund_type === REFUND_TYPES.BUSINESS
                                  ? refund.business_payee_name
                                  : refund.email}
                                )
                              </p>
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell>{refund && refund.phone}</TableCell>
                        <TableCell>
                          {parseFloat(refund && refund.refund_amount).toFixed(
                            2,
                          )}
                        </TableCell>
                        <TableCell>{refund.external_id}</TableCell>
                        <TableCell>
                          {refund && refund.transaction_status && (
                            <div
                              style={{
                                cursor: 'pointer',
                                color:
                                  refund &&
                                  refund.transaction_status == 'Completed'
                                    ? ''
                                    : '#ff0000',
                              }}>
                              {refund && refund.transaction_status}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('achPayments.table.noData')}</TableCell>
                    </TableRow>
                  </TableBody>
                )}
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[
                        5,
                        10,
                        25,
                        { label: t('achPayments.pagination.all'), value: -1 },
                      ]}
                      colSpan={3}
                      count={payments.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default AchPayments;
